import React, { useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Link from "arena/components/Link";
import ShortyContentInfoDialog from "./ShortyContentInfoDialog";
import { stripHtml } from "core/utils/string";

export default function ShortyContentInfo({
  content,
  hideShortyTitle: hideShortyTitleProp,
  titleVariant = "h6",
  descriptionVariant = "body2",
  sx = {},
}) {
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);

  if (!content) return null;

  const handleClick = () => {
    setInfoDialogOpen(true);
  };

  const hideShortyTitle = hideShortyTitleProp ?? content.hideShortyTitle;

  return (
    <>
      <Box
        sx={{
          cursor: "pointer",
          ...sx,
        }}
        onClick={handleClick}
      >
        {/* Заголовок */}
        {!hideShortyTitle && (
          <Typography
            sx={{
              color: "white",
              textShadow: "0 2px 4px rgba(0,0,0,0.8), 0 0 8px rgba(0,0,0,0.6)",
              fontWeight: "bold",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            variant={titleVariant}
          >
            {stripHtml(content.title)}
          </Typography>
        )}

        {/* Описание */}
        {content.description && (
          <Typography
            sx={{
              color: "white",
              textShadow: "0 2px 4px rgba(0,0,0,0.8), 0 0 8px rgba(0,0,0,0.6)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              mt: 0.5,
            }}
            variant={descriptionVariant}
          >
            {stripHtml(content.description)}
          </Typography>
        )}

        {/* Связанное видео */}
        {content.linkedVideo && (
          <Box
            component={Link}
            to={content.linkedVideo.siteUrl}
            onClick={(e) => {
              e.stopPropagation();
            }}
            sx={{
              mt: 1,
              display: "flex",
              gap: 1,
              alignItems: "center",
              textDecoration: "none",
              cursor: "pointer",
              "&:hover": {
                opacity: 0.85,
              },
            }}
          >
            <Box
              component="img"
              src={
                content.linkedVideo.thumbnails?.[0]?.webpUrl ||
                content.linkedVideo.thumbnails?.[0]?.url
              }
              alt={content.linkedVideo.title}
              sx={{
                height: 48,
                width: "auto",
                borderRadius: 1,
                objectFit: "cover",
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color: "white",
                textShadow: "0 2px 4px rgba(0,0,0,0.8)",
              }}
            >
              {content.linkedVideo.title.length > 30
                ? `${content.linkedVideo.title.substring(0, 30)}...`
                : content.linkedVideo.title}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Диалог с информацией о контенте */}
      <ShortyContentInfoDialog
        content={content}
        hideShortyTitle={hideShortyTitle}
        open={infoDialogOpen}
        onClose={() => setInfoDialogOpen(false)}
      />
    </>
  );
}

ShortyContentInfo.propTypes = {
  content: PropTypes.shape({
    title: PropTypes.string.isRequired,
    hideShortyTitle: PropTypes.bool,
    description: PropTypes.string,
    linkedVideo: PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string,
      siteUrl: PropTypes.string,
      thumbnails: PropTypes.arrayOf(
        PropTypes.shape({
          format: PropTypes.string,
          url: PropTypes.string,
          webpUrl: PropTypes.string,
        })
      ),
    }),
    sport: PropTypes.shape({
      title: PropTypes.string,
      name: PropTypes.string,
    }),
    league: PropTypes.shape({
      title: PropTypes.string,
      name: PropTypes.string,
    }),
    teams: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        name: PropTypes.string,
      })
    ),
    date: PropTypes.string,
  }),
  hideShortyTitle: PropTypes.bool,
  titleVariant: PropTypes.string,
  descriptionVariant: PropTypes.string,
  sx: PropTypes.object,
};
